<template>
  <div>
    <validation-observer ref="validator">
      <b-form @submit.prevent="createOrUpdate">
        <b-form-group>
          <select-suggestion
            :label="$t('reporting_channel.workflow_config.legal_entity')"
            column="company_name,trading_name,cnpj"
            model="LegalEntity"
            v-model="selectedLegalEntity"
            :appendClearOption="true"
            :placeholderText="
              $t('reporting_channel.workflow_config.legal_entity_placeholder')
            "
            :itemTextFormat="legalEntityItemTextFormat"
          />
          <small class="text-muted">{{
            $t('reporting_channel.workflow_config.legal_entity_hint')
          }}</small>
          <small class="text-danger" v-if="errors.legal_entity_id">{{
            errors.legal_entity_id[0]
          }}</small>
        </b-form-group>

        <b-row>
          <b-col md="12">
            <b-form-group
              :label="$t('reporting_channel.workflow_config.description')"
              label-for="description"
            >
              <b-form-textarea
                id="description"
                v-model="model.description"
                rows="3"
                :placeholder="
                  $t(
                    'reporting_channel.workflow_config.description_placeholder'
                  )
                "
                :state="errors.description ? false : null"
              />
              <small class="text-danger" v-if="errors.description">{{
                errors.description[0]
              }}</small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group
              label="Realizar tratamento após triagem"
              label-for="treat_after_screening"
            >
              <b-form-checkbox
                id="treat_after_screening"
                v-model="model.treat_after_screening"
                switch
                class="mr-n2"
              >
              </b-form-checkbox>
              <small class="text-danger" v-if="errors.treat_after_screening">{{
                errors.treat_after_screening[0]
              }}</small>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="mt-3 flex justify-end">
          <b-button type="submit" variant="primary" :disabled="!validateForm">
            {{ id ? $t('action.save') : $t('action.create') }}
          </b-button>
          <b-button
            type="button"
            variant="outline-secondary"
            @click="handleBack"
            class="ml-2"
          >
            {{ $t('action.back') }}
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormTextarea,
  BFormCheckbox,
  BRow,
  BCol
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import SelectSuggestion from '@/components/SelectSuggestion.vue'
import ReportingChannelWorkflowConfigService from '@/services/api/reporting-channel/ReportingChannelWorkflowConfigService'

export default {
  name: 'BasicDataTab',
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormTextarea,
    BFormCheckbox,
    BRow,
    BCol,
    ValidationObserver,
    SelectSuggestion
  },
  props: {
    id: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    service: null,
    model: {
      description: null,
      legal_entity_id: null,
      treat_after_screening: false
    },
    errors: {},
    selectedLegalEntity: null
  }),
  computed: {
    validateForm() {
      return this.model.description && this.model.description.trim().length > 0
    }
  },
  beforeMount() {
    this.service = ReportingChannelWorkflowConfigService.build(this.$vs)
  },
  mounted() {
    if (this.id) {
      this.loadData(this.id)
    }
  },
  methods: {
    loadData(id) {
      this.$vs.loading()
      this.service
        .read(id)
        .then(
          response => {
            this.model = {
              description: response.description,
              legal_entity_id: response.legal_entity_id,
              treat_after_screening: response.treat_after_screening || false
            }
            // Se houver legal_entity_id, carrega o objeto legal entity
            if (response.legal_entity_id && response.legal_entity) {
              this.selectedLegalEntity = response.legal_entity
            } else if (response.legal_entity_id) {
              // Se só tiver o ID, precisa buscar o objeto
              this.selectedLegalEntity = { id: response.legal_entity_id }
            } else {
              this.selectedLegalEntity = null
            }
          },
          error => {
            this.notifyError(this.$vs, 'Não foi possível carregar os dados')
          }
        )
        .finally(() => {
          this.$vs.loading.close()
        })
    },
    legalEntityItemTextFormat(model) {
      if (this._.isEmpty(model)) return ''

      let string = `${model.company_name} - ${model.trading_name}`

      if (model.cnpj) {
        string = `${string} (${this.cnpjFormat(model.cnpj)})`
      }

      return string
    },
    cnpjFormat(cnpj) {
      if (!cnpj) return ''
      // Remove caracteres não numéricos
      const numbers = cnpj.replace(/\D/g, '')
      // Formata como XX.XXX.XXX/XXXX-XX
      return numbers.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      )
    },
    createOrUpdate() {
      this.errors = {}
      this.$vs.loading()

      const data = {
        description: this.model.description,
        legal_entity_id: this._.get(this.selectedLegalEntity, 'id') || null,
        treat_after_screening: this.model.treat_after_screening || false
      }

      // Se for edição, adiciona o id ao objeto data
      if (this.id) {
        data.id = this.id
      }

      const promise = this.id
        ? this.service.update(data)
        : this.service.create(data)

      promise
        .then(
          response => {
            this.notifySuccess(this.$vs, 'Configuração salva com sucesso!')
            if (this.id) {
              // Se for edição, recarrega os dados
              this.loadData(this.id)
              // Emite evento para atualizar o id no componente pai se necessário
              this.$emit('saved', response.id || this.id)
            } else {
              // Se for criação, redireciona para a tela de edição
              const newId = response.id || response.data?.id
              this.$router.push({
                name: 'handling_status_configuration_edit',
                params: { id: newId }
              })
            }
          },
          error => {
            if (
              error.response &&
              error.response.data &&
              error.response.data.errors
            ) {
              this.errors = error.response.data.errors
            } else {
              this.notifyError(
                this.$vs,
                'Não foi possível salvar a configuração'
              )
            }
          }
        )
        .finally(() => {
          this.$vs.loading.close()
        })
    },
    handleBack() {
      this.$router.push({ name: 'handling_status_configuration_index' })
    }
  }
}
</script>

<style scoped></style>
